import { useMemo } from 'react';
import dagre from 'dagre';
import type { Node, Edge } from '@xyflow/react';

// Node footprint passed to dagre. Slightly larger than the rendered box
// (~160-200px wide × 50-60px tall) so siblings on a crowded row don't
// overlap each other's text. Reported "tangled" rendering 2026-05-14.
const NODE_WIDTH = 220;
const NODE_HEIGHT = 80;

export function useOrgChartLayout(
  nodes: Node[],
  edges: Edge[],
): { layoutedNodes: Node[]; layoutedEdges: Edge[] } {
  return useMemo(() => {
    if (nodes.length === 0) return { layoutedNodes: [], layoutedEdges: edges };

    const g = new dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    // Breathing room: wider sibling gap so 10+ sections on the same rank
    // don't bleed into each other, and a taller rank gap so the parent
    // edges have a clean diagonal instead of stacking on top of children.
    g.setGraph({
      rankdir: 'TB',
      nodesep: 80,
      ranksep: 140,
      edgesep: 30,
      marginx: 40,
      marginy: 40,
      align: 'UL',
      ranker: 'tight-tree',
    });

    for (const node of nodes) {
      g.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
    }
    for (const edge of edges) {
      g.setEdge(edge.source, edge.target);
    }

    dagre.layout(g);

    const layoutedNodes = nodes.map((node) => {
      const dagreNode = g.node(node.id);
      return {
        ...node,
        position: {
          x: dagreNode.x - NODE_WIDTH / 2,
          y: dagreNode.y - NODE_HEIGHT / 2,
        },
      };
    });

    return { layoutedNodes, layoutedEdges: edges };
  }, [nodes, edges]);
}
